/**
 * Assignment Setup Component
 *
 * CRUD interface for managing assignment definitions for a specific course.
 * Shows assignment details, assigned users/tags, and provides edit/delete actions.
 */

import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AssignmentDefinition } from '../../apis/api-client';
import { assignmentDefinitionsClient } from '../../apis/client-instances';
import { alert } from '../../hooks/function';
import { formatDate, formatDateTime } from '../../utils/roleHelpers';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '../ui';
import { EditIcon, DeleteIcon } from '../icons';
import FloatingActionButton from '../common/FloatingActionButton';
import ConfirmDialog from '../ConfirmDialog';
import EditAssignmentDialog from './EditAssignmentDialog';

interface AssignmentSetupProps {
  courseId?: number | null;
  searchQuery?: string;
  workspaceId?: number | null;
  /** When true, the floating "+" action button is suppressed. The
   *  parent is expected to provide its own trigger (e.g., a button in
   *  the page header) and wire it via `addHandlerRef` below. Leaves
   *  the in-course AssignmentSetup unchanged — that surface still gets
   *  its FAB. */
  hideAddFab?: boolean;
  /** Optional ref the parent can use to invoke the "create assignment"
   *  flow from outside this component. We assign `handleAdd` to the
   *  ref's `current` on mount, clear it on unmount. Lets the parent
   *  surface its own create-button without lifting all the dialog
   *  state out of this component. */
  addHandlerRef?: React.MutableRefObject<(() => void) | null>;
}

export default function AssignmentSetup({ courseId, searchQuery, workspaceId, hideAddFab, addHandlerRef }: AssignmentSetupProps) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<AssignmentDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<AssignmentDefinition | null>(null);

  useEffect(() => {
    fetchAssignments();
  }, [courseId, workspaceId]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const result = !courseId
        ? await assignmentDefinitionsClient.getAllDefinitions(workspaceId || undefined, undefined)
        : await assignmentDefinitionsClient.getCourseDefinitions(courseId);
      setAssignments(result);
    } catch (error: any) {
      alert(enqueueSnackbar, 'Failed to load assignments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (assignment: AssignmentDefinition) => {
    setSelectedAssignment(assignment);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedAssignment(null);
    setDialogOpen(true);
  };

  // Expose the create-flow trigger to the parent when an addHandlerRef
  // is supplied. The Assignments page hooks this up so its top-right
  // "Add Assignment" button can fire the same dialog the FAB used to.
  useEffect(() => {
    if (!addHandlerRef) return;
    addHandlerRef.current = handleAdd;
    return () => {
      addHandlerRef.current = null;
    };
    // handleAdd only depends on stable state setters, so a stable
    // identity isn't required — but capture the latest closure here
    // by re-registering whenever the ref itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addHandlerRef]);

  const handleDelete = (assignment: AssignmentDefinition) => {
    setSelectedAssignment(assignment);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAssignment?.id) return;

    try {
      await assignmentDefinitionsClient.deleteDefinition(selectedAssignment.id);
      alert(enqueueSnackbar, 'Assignment deleted successfully', 'success');
      fetchAssignments();
    } catch (error: any) {
      alert(enqueueSnackbar, error?.response?.data || 'Failed to delete assignment', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedAssignment(null);
    }
  };

  const renderAssignedTo = (assignment: AssignmentDefinition) => {
    const userCount = assignment.targetUserIds?.length || 0;
    const tagCount = assignment.targetTagIds?.length || 0;
    const groupCount = assignment.targetGroupIds?.length || 0;
    const roleCount = assignment.targetProfessionIds?.length || 0;

    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {userCount > 0 && (
          <Chip label={`${userCount} User${userCount !== 1 ? 's' : ''}`} size="small" />
        )}
        {tagCount > 0 && (
          <Chip
            label={`${tagCount} Tag${tagCount !== 1 ? 's' : ''}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {groupCount > 0 && (
          <Chip
            label={`${groupCount} Group${groupCount !== 1 ? 's' : ''}`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
        {roleCount > 0 && (
          <Chip
            label={`${roleCount} Role${roleCount !== 1 ? 's' : ''}`}
            size="small"
            sx={{
              bgcolor: 'rgba(122,90,0,0.10)',
              color: 'warning.softText',
              fontWeight: 600,
              border: '1px solid rgba(122,90,0,0.25)',
            }}
          />
        )}
        {userCount === 0 && tagCount === 0 && groupCount === 0 && roleCount === 0 && (
          <Typography variant="caption" color="text.secondary">
            None
          </Typography>
        )}
      </Box>
    );
  };

  // Apply search filter
  const filteredAssignments = assignments.filter((assignment) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const courseTitle = assignment.courseTitle?.toLowerCase() || '';
      const courseId = assignment.courseId?.toString() || '';
      return courseTitle.includes(query) || courseId.includes(query);
    }
    return true;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {filteredAssignments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary" variant="h6" gutterBottom>
            No assignments yet
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {!courseId
              ? 'Create an assignment to set deadlines for courses'
              : 'Create an assignment to set deadlines for users'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {!courseId && <TableCell>Course</TableCell>}
                <TableCell>Eligibility Start</TableCell>
                <TableCell>Deadline</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAssignments.map((assignment) => (
                <TableRow key={assignment.id} hover onClick={() => handleEdit(assignment)} sx={{ cursor: 'pointer' }}>
                  {!courseId && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/course/${assignment.courseId}`)}
                        sx={{ textAlign: 'left', fontWeight: 500 }}
                      >
                        {assignment.courseTitle || `Course #${assignment.courseId}`}
                      </Link>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2">
                      {assignment.startDate && formatDate(assignment.startDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(assignment.deadline)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {renderAssignedTo(assignment)}
                      {assignment.isOnboardingOnly && (
                        <Tooltip title="Only enrolls learners who join after the assignment was created. Existing learners are skipped.">
                          <Chip
                            label="Onboarding only"
                            size="small"
                            sx={{
                              alignSelf: 'flex-start',
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              bgcolor: 'rgba(var(--brand-rgb), 0.18)',
                              color: 'primary.main',
                              '& .MuiChip-label': { px: 0.85 },
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(assignment)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(assignment);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Floating Action Button — suppressed when the parent owns the
          create-trigger (e.g., the Assignments page's top-right button). */}
      {!hideAddFab && <FloatingActionButton onClick={handleAdd} />}

      {/* Dialogs */}
      <EditAssignmentDialog
        open={dialogOpen}
        courseId={courseId}
        assignment={selectedAssignment}
        onClose={() => {
          setDialogOpen(false);
          setSelectedAssignment(null);
        }}
        onSuccess={fetchAssignments}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Assignment"
        content={`Are you sure you want to delete this assignment? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedAssignment(null);
        }}
      />
    </Box>
  );
}
